import React from "react";

import dayjs from "dayjs";

import AirbyteApi from "apis/airbyte";
import { Typography } from "clearsense-ui/components";

import StatusTag from "./StatusTag";
import Sync from "./Sync";

import { getIcon } from "../utils";

const fetchSourceDefinitionDetails = async sourceDefinitionId => {
  try {
    const { data } = await AirbyteApi.fetchSourceDefinitionDetails({
      sourceDefinitionId,
    });
    return data;
  } catch (error) {
    logger.error(error);
    return undefined;
  }
};

const fetchDestinationDefinitionDetails = async destinationDefinitionId => {
  try {
    const { data } = await AirbyteApi.fetchDestinationDefinitionDetails({
      destinationDefinitionId,
    });
    return data;
  } catch (error) {
    logger.error(error);
    return undefined;
  }
};

const columns = [
  {
    dataIndex: "source",
    key: "source",
    title: "Source",
    fixed: "left",
    sorter: (a, b) => a.sourceName.localeCompare(b.sourceName),
    render: (_, record) => (
      <div className="flex space-x-2">
        {getIcon(record.sourceIcon)}
        <Typography data-cy="connection-source-name">
          {record.sourceName}
        </Typography>
      </div>
    ),
  },
  {
    dataIndex: "target",
    key: "target",
    title: "Target",
    sorter: (a, b) => a.destinationName.localeCompare(b.destinationName),
    render: (_, record) => (
      <div className="flex space-x-2">
        {getIcon(record.destinationIcon)}
        <Typography>{record.destinationName}</Typography>
      </div>
    ),
  },
  {
    dataIndex: "status",
    key: "status",
    title: "STATUS",
    width: 120,
    sorter: (a, b) => a.syncStatus.localeCompare(b.syncStatus),
    render: (_, record) => <StatusTag status={record.syncStatus} />,
  },
  {
    dataIndex: "lastSync",
    key: "lastSync",
    title: "Last Sync",
    width: 150,
    sorter: (a, b) => a.lastSync - b.lastSync,
    align: "center",
    render: (_, record) =>
      record.lastSync ? dayjs.unix(record.lastSync).fromNow() : "Not synced",
  },
  {
    dataIndex: "frequency",
    key: "frequency",
    title: "Frequency",
    width: 110,
    align: "center",
    render: (_, { frequency }) => (frequency ? frequency.humanize() : "Manual"),
    sorter: (a, b) => {
      if (!a.frequency) return 1;

      if (!b.frequency) return -1;

      return a.frequency.asMilliseconds() - b.frequency.asMilliseconds();
    },
  },
  {
    title: "Actions",
    dataIndex: "sync",
    key: "sync",
    width: 150,
    align: "center",
    render: (_, record) => (
      <Sync connectionId={record.key} isSyncing={record.isSyncing} />
    ),
  },
];

const getConnectionsData = async connections =>
  await Promise.all(
    connections.map(async connection => {
      const [sourceDefinition, destinationDefinition] = await Promise.all([
        fetchSourceDefinitionDetails(connection.source.sourceDefinitionId),
        fetchDestinationDefinitionDetails(
          connection.destination.destinationDefinitionId
        ),
      ]);

      const frequency =
        connection?.schedule &&
        dayjs.duration(connection.schedule.units, connection.schedule.timeUnit);
      return {
        key: connection.connectionId,
        sourceName: connection.source.name,
        destinationName: connection.destination.name,
        sourceIcon: sourceDefinition?.icon,
        destinationIcon: destinationDefinition?.icon,
        syncStatus: connection.latestSyncJobStatus ?? "pending",
        isSyncing: connection.isSyncing,
        lastSync: connection?.latestSyncJobCreatedAt,
        frequency,
      };
    })
  );

export { getConnectionsData, columns };
